import { getHoleStrokes } from '@polf/shared/scoring'

import type { LeaderboardHoleScoreFragment } from '../../__generated__/hooks'
import { FILLED_TONES } from '../../pages/Scorecard/components/marks'
import ScoreMark from '../../pages/Scorecard/components/ScoreMark'
import StrokeDots from '../../pages/Scorecard/components/StrokeDots'
import { termFor } from '../../util/term'

export type CourseHoleSummary = {
  number: number
  par: number
  /** Stroke index (1–18) — drives the per-hole handicap-stroke dot. */
  index: number
}

/** Par-relative label: `E` / `+2` / `-1`. */
const fmtToPar = (n: number): string => {
  if (n === 0) return 'E'
  return n > 0 ? `+${n}` : String(n)
}

/**
 * Compact single-column scorecard rendered inside `DetailSheet`. One
 * row per hole — hole number + par + gross score with a score mark
 * behind it (mark grammar from `Scorecard/components/ScoreMark.tsx`).
 *
 * Reuses the same mark visuals as the full scorecard so the row at a
 * glance carries the same "this was a birdie" signal.
 *
 * Renders ALL course holes (not just scored ones). The leaderboard
 * resolver only ships `PolfEventScore` rows for holes that have a
 * registered score, so we iterate the canonical course-hole list
 * (`courseHoles`) and look up the entry's score by hole number.
 * Unscored holes render with a `—` placeholder so the player can see
 * what's still outstanding.
 *
 * The last column follows the ranking: stableford points (default) or,
 * under net-strokes (`isNet`), the hole's par-relative score. When a
 * `strokesAllowance` is supplied the row stamps a red handicap-stroke
 * dot on every hole the player gets a stroke on — played or not — so
 * the card doubles as a "where do I get shots" reference.
 *
 * Front-9/back-9 split with subtotal lines, total at the bottom (only
 * counts holes with a registered score).
 */
const MiniScorecard = ({
  courseHoles,
  scores,
  isNet = false,
  strokesAllowance,
}: {
  /** Canonical course-hole list (typically 18). Defines the row order. */
  courseHoles: readonly CourseHoleSummary[]
  /** Registered scores from the leaderboard entry — may be partial. */
  scores: readonly LeaderboardHoleScoreFragment[]
  /** Net-strokes ranking → last column shows par-relative score, not points. */
  isNet?: boolean
  /** Course-handicap allowance; drives the per-hole stroke dot when set. */
  strokesAllowance?: number
}) => {
  const scoreByHole = new Map(scores.map((s) => [s.hole.number, s]))
  const ordered = [...courseHoles].sort((a, b) => a.number - b.number)
  const front = ordered.filter((h) => h.number <= 9)
  const back = ordered.filter((h) => h.number > 9)
  const sumScored = (holes: readonly CourseHoleSummary[]) =>
    holes.reduce((acc, h) => acc + (scoreByHole.get(h.number)?.score ?? 0), 0)
  const sumPoints = (holes: readonly CourseHoleSummary[]) =>
    holes.reduce(
      (acc, h) => acc + (scoreByHole.get(h.number)?.stablefordPoints ?? 0),
      0
    )
  const totalScored = scores.reduce((acc, s) => acc + (s.score ?? 0), 0)
  const hasAnyScore = scores.some((s) => s.score != null)

  // Net-to-par for a hole = gross − handicap strokes − par. null when unplayed.
  const netForHole = (h: CourseHoleSummary): number | null => {
    const s = scoreByHole.get(h.number)
    if (s?.score == null) return null
    const given =
      strokesAllowance != null ? getHoleStrokes(strokesAllowance, h.index) : 0
    return s.score - given - h.par
  }
  const sumNet = (holes: readonly CourseHoleSummary[]): number =>
    holes.reduce((acc, h) => {
      const n = netForHole(h)
      return n != null ? acc + n : acc
    }, 0)

  // Under net-strokes the last column is a LIVING score: each played hole shows
  // the running net-to-par through that hole (not the hole's isolated diff), so
  // it evolves like the stableford points total does as the round progresses.
  const runningNet = new Map<number, number>()
  if (isNet) {
    let acc = 0
    for (const h of ordered) {
      const n = netForHole(h)
      if (n != null) {
        acc += n
        runningNet.set(h.number, acc)
      }
    }
  }

  // Per-row last-column string: running net-to-par (net) or points (stableford).
  const lastColFor = (h: CourseHoleSummary): string => {
    const s = scoreByHole.get(h.number)
    if (s?.score == null) return ''
    if (isNet) {
      const r = runningNet.get(h.number)
      return r != null ? fmtToPar(r) : ''
    }
    return String(s.stablefordPoints ?? 0)
  }

  // Subtotal last-column value: segment net-to-par (net) or points total.
  const colValue = (holes: readonly CourseHoleSummary[]): string =>
    isNet ? fmtToPar(sumNet(holes)) : String(sumPoints(holes))

  return (
    <div className="rounded-lg overflow-hidden">
      <Header isNet={isNet} />
      <ol>
        {front.map((h) => (
          <Row
            key={h.number}
            hole={h}
            entry={scoreByHole.get(h.number)}
            strokesAllowance={strokesAllowance}
            lastCol={lastColFor(h)}
          />
        ))}
        {front.length > 0 ? (
          <Subtotal
            label="Ut"
            strokes={sumScored(front)}
            value={colValue(front)}
          />
        ) : null}
        {back.map((h) => (
          <Row
            key={h.number}
            hole={h}
            entry={scoreByHole.get(h.number)}
            strokesAllowance={strokesAllowance}
            lastCol={lastColFor(h)}
          />
        ))}
        {back.length > 0 ? (
          <Subtotal
            label="Inn"
            strokes={sumScored(back)}
            value={colValue(back)}
          />
        ) : null}
        {hasAnyScore ? (
          <Subtotal
            label="Totalt"
            strokes={totalScored}
            value={colValue(ordered)}
            bold
          />
        ) : null}
      </ol>
    </div>
  )
}

const Header = ({ isNet }: { isNet: boolean }) => (
  <div className="grid grid-cols-[3rem_2.5rem_1fr_2.5rem] items-center px-3 py-1.5 border-b border-ink-200 bg-ink-100/40 up text-ink-500 text-[10px]">
    <span>Hull</span>
    <span className="text-right">Par</span>
    <span className="text-center">Score</span>
    <span className="text-right">{isNet ? '+/−' : 'Pts'}</span>
  </div>
)

const Row = ({
  hole,
  entry,
  strokesAllowance,
  lastCol,
}: {
  hole: CourseHoleSummary
  entry: LeaderboardHoleScoreFragment | undefined
  strokesAllowance?: number
  /** Precomputed last-column value (points or running net-to-par). */
  lastCol: string
}) => {
  const score = entry?.score ?? null
  const tone = score != null ? termFor(score, hole.par).tone : ('par' as const)
  const filled = FILLED_TONES.has(tone)
  // Deterministic from the round allowance + stroke index — known even before
  // the hole is played, so unplayed holes still show where shots fall.
  const strokesGiven =
    strokesAllowance != null ? getHoleStrokes(strokesAllowance, hole.index) : 0
  return (
    <li className="grid grid-cols-[3rem_2.5rem_1fr_2.5rem] items-center px-3 py-1.5 border-b border-ink-100 last:border-b-0">
      <span className="font-mono num text-sm text-ink-900">{hole.number}</span>
      <span className="font-mono num text-sm text-ink-500 text-right">
        {hole.par}
      </span>
      <span className="flex items-center justify-center">
        <span
          className="relative inline-flex items-center justify-center"
          style={{ width: 36, height: 36 }}
        >
          {score != null ? (
            <>
              <ScoreMark tone={tone} size={36} />
              {/* Anchor the stroke dots to the number (like the flight card),
                  not the mark box — keeps them off the mark's border. */}
              <span
                className={`relative font-mono num font-bold text-base ${
                  filled ? 'text-paper' : 'text-ink-900'
                }`}
              >
                {score}
                <StrokeDots count={strokesGiven} top={-2} right={-7} />
              </span>
            </>
          ) : (
            <span className="relative text-ink-300">
              —
              <StrokeDots count={strokesGiven} top={-2} right={-7} />
            </span>
          )}
        </span>
      </span>
      <span className="font-mono num text-sm text-ink-700 text-right">
        {lastCol}
      </span>
    </li>
  )
}

// Mirrors the 4-col header/row grid so the strokes total sits (centered) under
// the Score column and the points / par-relative total under the last column.
// `bold` is the grand-total emphasis; no larger font — bold reads as summary.
const Subtotal = ({
  label,
  strokes,
  value,
  bold = false,
}: {
  label: string
  strokes: number
  value: string
  bold?: boolean
}) => (
  <li
    className={`grid grid-cols-[3rem_2.5rem_1fr_2.5rem] items-center px-3 py-1.5 border-t border-ink-200 bg-ink-100/30 ${
      bold ? 'font-bold' : ''
    }`}
  >
    <span className="up text-ink-500 text-[10px]">{label}</span>
    <span />
    <span className="font-mono num text-sm text-ink-900 text-center">
      {strokes}
    </span>
    <span className="font-mono num text-sm text-ink-700 text-right">
      {value}
    </span>
  </li>
)

export default MiniScorecard
